import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { firestore } from "@/firebase";
import { raveConverter } from "@/converters";
import { useAuthContext } from "@/context/AuthContext";
import { Rave } from "@t3/shared";

export const LiteRavePresent = () => {
  const { raveId } = useParams<{ raveId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [rave, setRave] = useState<Rave | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !raveId) {
      navigate("/lite-raves/signup");
      return;
    }

    const fetchRave = async () => {
      try {
        const raveRef = doc(
          firestore,
          `company/${user.company}/raves/${raveId}`,
        );
        const raveDoc = await getDoc(raveRef.withConverter(raveConverter));

        const raveData = raveDoc.data();

        if (!raveData) {
          setError("Rave data not found");
          return;
        }

        // Verify this is a lite rave
        if (raveData.type !== "liteRaves") {
          setError("This is not a lite rave");
          return;
        }

        setRave(raveData);
      } catch (err) {
        console.error("Error fetching rave:", err);
        setError("Failed to load rave");
      } finally {
        setLoading(false);
      }
    };

    fetchRave();
  }, [raveId, user, navigate]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !rave) {
    return <Alert severity="error">{error || "Rave not found"}</Alert>;
  }

  const renderContent = () => {
    return rave.content.map((item, index) => {
      switch (item.type) {
        case "text":
          return (
            <Typography
              key={index}
              variant="body1"
              sx={{ mb: 2, whiteSpace: "pre-wrap" }}
            >
              {item.text}
            </Typography>
          );
        case "image":
          return (
            <Box
              key={index}
              component="img"
              src={item.image}
              alt="Rave content"
              sx={{
                width: "100%",
                maxHeight: "500px",
                objectFit: "contain",
                mb: 2,
                borderRadius: 1,
              }}
            />
          );
        case "video":
          return (
            <Box
              key={index}
              component="video"
              src={item.video}
              controls
              sx={{
                width: "100%",
                maxHeight: "500px",
                mb: 2,
                borderRadius: 1,
              }}
            />
          );
        case "audio":
          return (
            <Box key={index} sx={{ mb: 2 }}>
              <audio src={item.audio} controls style={{ width: "100%" }} />
            </Box>
          );
        default:
          return null;
      }
    });
  };

  return (
    <Box sx={{ maxWidth: 800, mx: "auto" }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          {/* Sender Info */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Avatar
              src={rave.sender.avatar || undefined}
              sx={{ width: 56, height: 56, mr: 2 }}
            >
              {rave.sender.name.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h6">{rave.sender.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {rave.createdAt.toFormat("MMM dd, yyyy")}
              </Typography>
            </Box>
          </Box>

          {/* Title */}
          {rave.title && (
            <Typography variant="h4" sx={{ mb: 2 }}>
              {rave.title}
            </Typography>
          )}

          {/* Description */}
          {rave.description && (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, fontStyle: "italic" }}
            >
              {rave.description}
            </Typography>
          )}

          {/* Content */}
          <Box sx={{ mt: 3 }}>{renderContent()}</Box>

          {/* Recipients */}
          {rave.recipientIds.length > 0 && (
            <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: "divider" }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Sent to:
              </Typography>
              {rave.recipientIds.map((recipientId) => {
                const recipient = rave.recipients[recipientId];
                return (
                  <Box
                    key={recipientId}
                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                  >
                    <Avatar
                      src={recipient.avatar || undefined}
                      sx={{ width: 32, height: 32, mr: 1 }}
                    >
                      {recipient.name.charAt(0)}
                    </Avatar>
                    <Typography variant="body2">{recipient.name}</Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
